import type React from 'react';

import type { Body, Meta, Uppy } from '@uppy/core';
import { useFormContext } from 'react-hook-form';

import { Button } from '@change/ds-core-ui';
import { InfoV2Icon } from '@change/ds-icons';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import {
	NAME_MAX_LENGTH,
	STATEMENT_MAX_LENGTH,
	TITLE_MAX_LENGTH,
} from '#src/app/pages/starterDashboardEndorsements/shared/consts';
import type { FocusedField } from '#src/app/shared/components/endorsements';
import type { RawMediaCropInput, UploaderMethodProps } from '#src/app/shared/components/imageUpload';

import { PhotoUploadSection } from './components/PhotoUploadSection';
import { TextFieldWithCounter } from './TextFieldWithCounter';

type EndorsementFormFieldsProps = {
	statementCharCount: number;
	uppy: Uppy<Meta, Body>;
	image: string | undefined;
	uploadErrorMessage?: TranslationKey | null;
	onFieldFocus: (field: FocusedField) => void;
	onFieldBlur: () => void;
	crop?: RawMediaCropInput;
	uploader?: UploaderMethodProps;
	onImageCrop?: (imageCrop: RawMediaCropInput | undefined) => void;
	onCroppedPreviewChange?: (url: string) => void;
	onHelpClick: () => void;
};

export const EndorsementFormFields: React.FC<EndorsementFormFieldsProps> = ({
	statementCharCount,
	uppy,
	image,
	uploadErrorMessage,
	onFieldFocus,
	onFieldBlur,
	crop,
	uploader,
	onImageCrop,
	onCroppedPreviewChange,
	onHelpClick,
}) => {
	const { translate } = useI18n();
	const formContext = useFormContext();

	// photoUrl has no real <input>, so the form error can go stale — use image (from useEndorsementImageUpload) as source of truth
	const photoError = image ? undefined : formContext.formState.errors.photoUrl;
	const photoErrorMessage = typeof photoError?.message === 'string' ? photoError.message : undefined;

	return (
		<>
			<TextFieldWithCounter
				name="name"
				label={translate('corgi.petition.starter_dashboard_v2.endorsements.add.name_label')}
				supportingText={translate('corgi.petition.starter_dashboard_v2.endorsements.add.name_text_support')}
				maxLength={NAME_MAX_LENGTH}
				testId="endorser-name-field"
				qaId="endorser-name-field"
				onFocus={() => onFieldFocus('name')}
				onBlur={onFieldBlur}
			/>

			<TextFieldWithCounter
				name="title"
				label={translate('corgi.petition.starter_dashboard_v2.endorsements.add.role_or_description_label')}
				supportingText={translate(
					'corgi.petition.starter_dashboard_v2.endorsements.add.role_or_description_text_support',
				)}
				maxLength={TITLE_MAX_LENGTH}
				testId="endorser-title-field"
				qaId="endorser-title-field"
				onFocus={() => onFieldFocus('title')}
				onBlur={onFieldBlur}
			/>

			<TextFieldWithCounter
				name="statement"
				inputElementType="textarea"
				label={
					<>
						{translate('corgi.petition.starter_dashboard_v2.endorsements.add.statement_support_label')}{' '}
						<span className="text-typography-weak font-normal">
							{translate('corgi.petition.starter_dashboard_v2.endorsements.add.optional_label')}
						</span>
					</>
				}
				supportingText={translate('corgi.petition.starter_dashboard_v2.endorsements.add.statement_text_support')}
				maxLength={STATEMENT_MAX_LENGTH}
				currentLength={statementCharCount}
				inputClassName="min-h-28"
				testId="endorser-statement-field"
				qaId="endorser-statement-field"
				onFocus={() => onFieldFocus('statement')}
				onBlur={onFieldBlur}
			/>

			{/* Photo Upload Section */}
			<PhotoUploadSection
				image={image}
				uppy={uppy}
				isInvalid={!!photoError}
				errorMessage={photoErrorMessage}
				uploadErrorMessage={uploadErrorMessage}
				crop={crop}
				uploader={uploader}
				onImageCrop={onImageCrop}
				onCroppedPreviewChange={onCroppedPreviewChange}
			/>

			<TextFieldWithCounter
				name="url"
				label={translate('corgi.petition.starter_dashboard_v2.endorsements.add.profile_or_link_label')}
				supportingText={translate('corgi.petition.starter_dashboard_v2.endorsements.add.profile_or_link_support_text')}
				testId="endorser-url-field"
				qaId="endorser-url-field"
				displayCounter={false}
				onFocus={() => onFieldFocus('url')}
				onBlur={onFieldBlur}
			/>

			{/* Help link for mobile */}
			<Button
				onClick={onHelpClick}
				data-testid="endorsement-help-link"
				data-qa="endorsement-help-link"
				size="small"
				variant="tertiary"
				leadingIcon={<InfoV2Icon className="text-typography-strong" size="medium" />}
				className="flex w-fit items-start gap-2 pr-1 pl-0 lg:hidden"
			>
				<Translate value="corgi.petition.starter_dashboard_v2.endorsements.add.view_endorsement_example" />
			</Button>
		</>
	);
};
